import { forwardRef, useMemo, useCallback, CSSProperties } from 'react';

import { CheckCircleFilled, LoadingOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { Input as AntdInput, ConfigProvider, InputProps, Typography } from 'antd';
import { InputRef, TextAreaProps } from 'antd/es/input';
import locale from 'antd/locale/ko_KR';

import { Colors, GrayColors } from '../../colors';
import { typographyStyle } from '../../style';
import { themeForClobe } from '../../style/constant';
import { Flex } from '../Flex';
import ExclamationCircleIcon from '../Icon/ExclamationCircle';

export type InputStatus = 'error' | 'warning' | 'success' | 'loading';

export type InputSize = 'small' | 'default' | 'large' | 'xlarge';

interface Props extends Omit<InputProps, 'ref'> {
  inputStatus?: InputStatus;
  disabled?: boolean;
  additionalText?: string;
  passwordType?: boolean;
  formatNumber?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  formatter?: (value: string) => string;
  deformatter?: (value: string) => string;
  inputSize?: InputSize;
  wrapperStyle?: CSSProperties;
}

const getInputStyles = (size: InputSize = 'default') => {
  switch (size) {
    case 'small':
      return {
        height: 24,
        borderRadius: 6,
        ...typographyStyle['13_400'],
        '&::placeholder': {
          ...typographyStyle['13_400'],
        },
      };
    case 'default':
      return {
        height: 32,
        borderRadius: 8,
        ...typographyStyle['14_400'],
        '&::placeholder': {
          ...typographyStyle['14_400'],
        },
      };
    case 'large':
      return {
        height: 40,
        borderRadius: 10,
        ...typographyStyle['16_400'],
        '&::placeholder': {
          ...typographyStyle['16_400'],
        },
      };
    case 'xlarge':
      return {
        height: 44,
        paddingLeft: 12,
        paddingRight: 12,
        borderRadius: 12,
        ...typographyStyle['16_400'],
        '&::placeholder': {
          ...typographyStyle['16_400'],
        },
      };
  }
};

const getColor = (inputType?: InputStatus) => {
  switch (inputType) {
    case 'error':
      return Colors.red500;
    case 'warning':
      return Colors.orange500;
    case 'success':
      return Colors.green500;
    default:
      return Colors.gray200;
  }
};

const getHoverColor = (inputType?: InputStatus) => {
  switch (inputType) {
    case 'error':
      return Colors.red500;
    case 'warning':
      return Colors.orange500;
    case 'success':
      return Colors.green500;
    default:
      return Colors.gray400;
  }
};

const getSuffixIcon = (inputType?: InputStatus) => {
  switch (inputType) {
    case 'error':
      return <ExclamationCircleIcon fill={Colors.red500} width={16} height={16} />;
    case 'warning':
      return <ExclamationCircleIcon fill={Colors.orange500} width={16} height={16} />;
    case 'success':
      return (
        <CheckCircleFilled
          style={{
            color: Colors.green500,
            fontSize: '16px',
          }}
        />
      );

    case 'loading':
      return (
        <LoadingOutlined
          style={{
            fontSize: '16px',
          }}
        />
      );

    default:
      return '';
  }
};

const formatNumberValue = (value: string) => {
  // Remove all non-digit characters
  const numbers = value.replace(/[^\d]/g, '');

  // Add commas for thousands
  return numbers.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const getNumericValue = (value: string) => {
  // Remove all non-digit characters
  const numbers = value.replace(/[^\d]/g, '');

  // Return undefined for empty string, otherwise convert to number
  return numbers === '' ? undefined : Number(numbers);
};

export const Input = forwardRef<InputRef, Props>(
  (
    {
      inputStatus,
      additionalText,
      suffix,
      formatNumber,
      onChange,
      value,
      formatter,
      deformatter,
      passwordType,
      inputSize = 'default',
      wrapperStyle,
      ...props
    },
    ref,
  ) => {
    const suffixIcon = useMemo(() => getSuffixIcon(inputStatus), [inputStatus]);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        let nextValue = e.target.value;
        if (deformatter) {
          nextValue = deformatter(nextValue);
        } else if (formatNumber) {
          nextValue = getNumericValue(nextValue)?.toString() ?? '';
        }
        onChange?.({
          ...e,
          target: { ...e.target, value: nextValue },
        });
      },
      [deformatter, formatNumber, onChange],
    );

    const displayValue = useMemo(() => {
      if (formatter && value != null) {
        return formatter(String(value));
      }
      if (formatNumber && value) {
        return formatNumberValue(String(value));
      }
      return value;
    }, [formatter, formatNumber, value]);

    const className = css(
      {
        backgroundColor: inputStatus == 'loading' ? GrayColors.gray200 : undefined,
        '& > input': {
          backgroundColor: inputStatus == 'loading' ? GrayColors.gray200 : undefined,
          '&:focus': {
            borderWidth: '2px !important',
          },
        },
        borderColor: getColor(inputStatus),
        boxShadow: 'none',
        '&:hover': {
          borderColor: `${getHoverColor(inputStatus)} !important`,
        },
        '&.ant-input-affix-wrapper-focused, &.ant-input-focused': {
          borderWidth: '1px !important',
          boxShadow: `0 0 0 1px ${getColor(inputStatus)} !important`,
        },
        '&.ant-input, &.ant-input-affix-wrapper': {
          transition: 'none !important',
        },
      },
      getInputStyles(inputSize),
    );

    return (
      <Flex direction="column" gap={additionalText ? 4 : 0} style={wrapperStyle}>
        {passwordType ? (
          <AntdInput.Password
            ref={ref}
            className={className}
            suffix={suffix || (formatNumber ? '원' : <div key="suffix">{suffixIcon}</div>)}
            onChange={handleChange}
            value={displayValue}
            {...props}
          />
        ) : (
          <AntdInput
            ref={ref}
            className={className}
            suffix={suffix || (formatNumber ? '원' : <div key="suffix">{suffixIcon}</div>)}
            onChange={handleChange}
            value={displayValue}
            {...props}
          />
        )}
        <Typography.Text
          style={{
            color: getColor(inputStatus),
            ...typographyStyle['14_400'],
          }}
        >
          {additionalText}
        </Typography.Text>
      </Flex>
    );
  },
);

Input.displayName = 'Input';

interface TextareaProps extends TextAreaProps {
  inputStatus?: InputStatus;
  disabled?: boolean;
  additionalText?: string;
}

export const TextArea: React.FC<TextareaProps> = ({ inputStatus, additionalText, ...props }) => {
  return (
    <ConfigProvider locale={locale} theme={{ ...themeForClobe }}>
      <Flex direction="column" gap={additionalText ? 4 : 0}>
        <AntdInput.TextArea
          className={css`
            &:hover {
              border-color: ${Colors.gray400} !important;
            }

            &:focus {
              border-color: ${Colors.gray400} !important;
              box-shadow: 0 0 0 1px ${Colors.gray400} !important;
            }
          `}
          {...props}
        />
        <Typography.Text
          style={{
            color: getColor(inputStatus),
            ...typographyStyle['14_400'],
          }}
        >
          {additionalText}
        </Typography.Text>
      </Flex>
    </ConfigProvider>
  );
};
